import { useEffect, useState } from "react";
import { Loader2, X, CreditCard, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { formatRupiah } from "@/components/booking/bookingMeta";

// Payment method picker: fetches active Duitku channels for the chosen amount+purpose,
// then creates the payment with the selected method and redirects to Duitku.
export default function PaymentMethodModal({ open, booking, purpose = "room", amount = null, onClose, onBack }) {
  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState([]);
  const [amt, setAmt] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    if (!open || !booking) return;
    setLoading(true); setError(null); setMethods([]);
    apiClient
      .get(`/payments/methods/${booking.id}`, { params: { payment_purpose: purpose, amount } })
      .then((r) => { setMethods(r.data?.methods || []); setAmt(r.data?.amount ?? amount); })
      .catch((err) => setError(formatApiErrorDetail(err?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, booking, purpose, amount]);

  if (!open || !booking) return null;

  const choose = async (code) => {
    setPaying(code);
    try {
      const { data } = await apiClient.post("/payments/duitku", {
        booking_id: booking.id, payment_purpose: purpose, payment_method: code, amount,
      });
      if (data.payment_url) window.location.assign(data.payment_url);
      else { toast.info("Pembayaran sedang disiapkan."); onClose?.(); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setPaying(null);
    }
  };

  const title = purpose === "deposit" ? "Bayar Deposit"
    : purpose === "room_and_deposit" ? "Bayar Kamar + Deposit" : "Bayar Harga Kamar";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="payment-method-modal">
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            {onBack && (
              <button onClick={onBack} data-testid="payment-method-back" className="text-muted-foreground hover:text-foreground">
                <ArrowLeft size={18} />
              </button>
            )}
            <div>
              <h2 className="font-heading text-lg font-bold">{title}</h2>
              {amt != null && (
                <p className="text-sm text-muted-foreground">Nominal: <span className="font-semibold text-primary" data-testid="payment-method-amount">{formatRupiah(amt)}</span></p>
              )}
            </div>
          </div>
          <button onClick={onClose} data-testid="payment-method-close" className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
        </div>

        <div className="p-4 overflow-y-auto">
          {loading ? (
            <div className="grid place-items-center py-12"><Loader2 className="animate-spin text-primary" size={26} /></div>
          ) : error ? (
            <p className="text-sm text-destructive text-center py-8" data-testid="payment-method-error">{error}</p>
          ) : methods.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Tidak ada metode pembayaran tersedia.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2" data-testid="payment-method-list">
              {methods.map((m) => (
                <button key={m.code} onClick={() => choose(m.code)} disabled={!!paying}
                  data-testid={`payment-method-${m.code}`}
                  className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5 text-left hover:border-primary hover:bg-secondary/40 transition-colors disabled:opacity-50">
                  {m.image ? (
                    <img src={m.image} alt={m.name} className="h-6 w-10 object-contain" />
                  ) : (
                    <CreditCard size={20} className="text-muted-foreground" />
                  )}
                  <span className="flex-1 text-sm font-medium">{m.name}</span>
                  {paying === m.code && <Loader2 size={16} className="animate-spin text-primary" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
